// FUNNEL-4 — **WHAT A CHAT SURFACE HOLDS ABOUT HER ONE ALLOTMENT, DECLARED ONCE.**
//
// The drawer and the page are ONE MIND BEHIND TWO DOORS by standing law, and the first draft of this
// round gave each of them its own copy of the state, its own presence guard and its own derivation —
// six lines apiece, twice. That is the shape this house has paid for over and over (R28/R29), and
// here the copy that drifts decides whether a member with turns left is shown a wall.
//
// >>> THE PRESENCE GUARD IS THE WHOLE REASON THIS IS A HOOK RATHER THAN TWO `useState` CALLS. <<<
// `undefined` on the wire is *this answer said nothing about an allotment* — which is EVERY paying
// member's turn and every paying member's thread read — and it may never wipe a counter she is
// looking at. `null` is the server saying she holds none. Read those two as one (R53: presence,
// never truthiness) and a paid member's ordinary answer clears a free member's counter mid-sentence.
//
// It lives in `src/lib` beside `shelf-overflow.ts`, which is the precedent for a hook here: the
// entry requirement for that folder is *no imports but `import type`* for the PURE laws a `.mjs`
// gate loads and runs, and a hook is React by definition — so what it holds is the WIRING, while the
// decision it delegates to (`freeTurnView`) stays in the import-free law the gate really runs.
import { useState } from "react";
import { freeTurnView } from "../lalaFreeTurns.js";

export type FreeTurnsState = {
  /** Exactly what the server last said, for the notice component to read. */
  payload: unknown;
  /** `counter` / `wall` / null — the law's answer, never a comparison typed at a call site. */
  view: ReturnType<typeof freeTurnView>;
  /** W6-2's composer lock, at the wall rather than at the cap. */
  atWall: boolean;
  /** Take what an answer said, and ignore an answer that said nothing. */
  take: (next: unknown) => void;
  /** Does the answer in hand put her at the wall? `atWall` is a render behind inside the handler. */
  wallFrom: (next: unknown) => boolean;
};

export function useFreeTurns(): FreeTurnsState {
  const [payload, setPayload] = useState<unknown>(null);
  const view = freeTurnView(payload);
  return {
    payload,
    view,
    atWall: view?.kind === "wall",
    take: (next: unknown) => { if (next !== undefined) setPayload(next); },
    // >>> **WHAT AN ANSWER JUST IN HAND SAYS, WHICH `atWall` CANNOT SAY YET (2026-08-25).** <<<
    // `take` sets state, so inside the handler that received the answer `atWall` is still the
    // PREVIOUS render's value — and the one moment a caller needs this is exactly there, on the
    // fifth answer, before deciding whether to fetch again. Measured: three seconds after her fifth
    // answer the Ask Lala page re-read `/api/lala/conversations`, `freeTurnsDoorOpen` had closed
    // with the allotment, the read was refused 402, and T8-3's interceptor NAVIGATED her to the
    // membership ladder — so her wall and its gold button were never on screen at all. It reads the
    // SAME `freeTurnView` the counter and the wall are drawn from, so there is no second answer to
    // *is she at the wall* for the two to drift apart on.
    wallFrom: (next: unknown) => freeTurnView(next)?.kind === "wall",
  };
}
